import { useMemo, useRef, useState } from 'react';
import { createFileRoute, useBlocker, useRouter } from '@tanstack/react-router';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { RoutePermissionGuard } from '@/components/molecules';
import {
  VisitRequestForm,
  type VisitRequestFormCustomer,
} from '@/components/organisms/scheduler/VisitRequestForm';
import { useCreateBookingFlow } from '@/hooks/scheduler/useCreateBookingFlow';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useContactSearch } from '@/hooks/contacts/useContactSearch';
import { contactHasPhone, normalizeContactPhoneToE164 } from '@/lib/contacts/phone';
import { dateKeyInTimeZone, dateStringToDate } from '@/lib/date-values';
import { apiPost, operationsClient } from '@/lib/api';
import { membershipEndpoints, recurringJobEndpoints } from '@/lib/api/endpoints';
import { bookingReturnDestination } from '@/lib/bookings/bookingReturnDestination';
import { toast } from 'sonner';

const searchSchema = z.object({
  returnTo: z.enum(['requests', 'service-calls']).optional(),
  date: z.string().optional(),
  technicianId: z.string().optional(),
  startAt: z.string().optional(),
  // Prefill from a live call: the matched contact and the raw number, plus the
  // Telnyx call id so the created request can be linked back to the call.
  contactId: z.string().optional(),
  contactName: z.string().optional(),
  contactPhone: z.string().optional(),
  callControlId: z.string().optional(),
  /** Epoch ms of the originating call, to match its own log by time. */
  callAt: z.string().optional(),
  retentionEntity: z.enum(['membership', 'job']).optional(),
  retentionId: z.string().optional(),
  retentionOfferType: z.string().optional(),
  retentionEntitlementId: z.string().optional(),
  retentionReasonCategory: z.string().optional(),
  retentionReasonText: z.string().optional(),
});

export const Route = createFileRoute('/_dashboard/dashboard/bookings/new')({
  validateSearch: searchSchema,
  component: NewBookingPage,
});

function NewBookingPage() {
  const router = useRouter();
  const {
    returnTo,
    date: dateParam,
    technicianId,
    startAt,
    contactId,
    contactName,
    contactPhone,
    callControlId,
    callAt,
    retentionEntity,
    retentionId,
    retentionOfferType,
    retentionEntitlementId,
    retentionReasonCategory,
    retentionReasonText,
  } = Route.useSearch();
  const bookingFlow = useCreateBookingFlow();
  const companyTimezone = useCompanyTimezone();

  // NOT-551 — guard against navigating away (Cancel, sidebar, back) and silently
  // discarding a partially filled service call.
  const [isDirty, setIsDirty] = useState(false);
  const allowNavigationRef = useRef(false);
  const blocker = useBlocker({
    disabled: !isDirty,
    enableBeforeUnload: false,
    withResolver: true,
    shouldBlockFn: () => {
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        return false;
      }
      return true;
    },
  });

  // A call gives us a NUMBER but not always a contact id — the caller-ID lookup
  // (contactId) only lands when the screen-pop SSE matched, and Telnyx never
  // sends an id itself. So when we arrive with just a number, resolve the saved
  // contact by phone here (the same search the form runs on submit), so a known
  // customer prefills as a linked contact instead of a manual entry.
  const phoneE164 = contactPhone ? normalizeContactPhoneToE164(contactPhone) : null;
  const resolveByPhone = !contactId && Boolean(phoneE164);
  const phoneSearch = useContactSearch(phoneE164 ?? '', 5, { enabled: resolveByPhone });
  const matchedByPhone = useMemo(() => {
    if (!resolveByPhone || !phoneE164) return null;
    // Any of the contact's numbers, not only the primary: the caller may be
    // ringing from the mobile or a second line the search matched them on.
    return phoneSearch.data?.data.find((c) => contactHasPhone(c, phoneE164)) ?? null;
  }, [resolveByPhone, phoneE164, phoneSearch.data]);

  const resolvedContactId = contactId ?? matchedByPhone?.id ?? null;

  const initialCustomer = useMemo<VisitRequestFormCustomer | null>(() => {
    if (!resolvedContactId && !contactPhone) return null;
    return {
      contactId: resolvedContactId,
      name: matchedByPhone?.displayName ?? contactName ?? '',
      phone: contactPhone ?? null,
    };
  }, [resolvedContactId, matchedByPhone, contactName, contactPhone]);

  // Wait for the phone→contact resolution before mounting the form: the form
  // seeds its customer from `initialCustomer` once, on mount, so a contactId that
  // arrives later wouldn't take. A brief blank is better than a missed prefill.
  const resolvingContact = resolveByPhone && phoneSearch.isLoading;

  const initialDateRange = useMemo(() => {
    const dateString = dateParam
      ? dateStringToDate(dateParam)
        ? dateParam
        : ''
      : companyTimezone
        ? dateKeyInTimeZone(startAt, companyTimezone)
        : '';
    if (!dateString) return null;
    return { from: dateString, to: dateString };
  }, [companyTimezone, dateParam, startAt]);

  const onBack = () =>
    void router.navigate({
      // Existing call/retention entry points do not carry an origin yet. Keep
      // the request queue as the safe fallback and opt into the service-call
      // lens only from its dedicated "New service call" action.
      to: returnTo === 'service-calls' ? '/dashboard/jobs/service-calls' : '/dashboard/bookings',
    });

  async function handleSubmit(values: Parameters<typeof bookingFlow.submit>[0]) {
    const result = await bookingFlow.submit(values);
    if (retentionEntity && retentionId && retentionOfferType === 'SCHEDULE_INCLUDED_SERVICE') {
      const endpoint =
        retentionEntity === 'membership'
          ? membershipEndpoints.saveOffer(retentionId)
          : recurringJobEndpoints.saveOffer(retentionId);
      try {
        if (retentionEntity !== 'membership' || !retentionEntitlementId || !result.jobId) {
          throw new Error('The included benefit could not be linked to the created service call.');
        }
        await apiPost(
          operationsClient,
          `/memberships/${retentionId}/entitlements/${retentionEntitlementId}/schedule`,
          {
            jobId: result.jobId,
            scheduledDate: values.selectedDay?.date ?? values.selectedSlot?.startAt,
          },
        );
        await apiPost(operationsClient, endpoint, {
          offerType: retentionOfferType,
          reasonCategory: retentionReasonCategory,
          reasonText: retentionReasonText,
          accepted: true,
          outcome: 'SAVED',
        });
        toast.success('Cancellation avoided - included service requested');
      } catch {
        toast.warning(
          'The service request was created, but the membership benefit was not reserved. Review it before completing the cancellation save.',
        );
      }
    }
    // The record was created — this navigation is not a discard.
    allowNavigationRef.current = true;
    void router.navigate({
      to: bookingReturnDestination(result.scheduled, returnTo),
    });
  }

  if (resolvingContact) {
    return <div className="p-6 text-sm text-muted-foreground">Loading customer…</div>;
  }

  return (
    <RoutePermissionGuard
      permissions="bookings:create"
      message="You do not have permission to create service requests."
    >
      <VisitRequestForm
        // Remount per originating call: /bookings/new is one route, so navigating
        // from one call's request to another's keeps the component mounted and its
        // filled note fields (and resolved AI summary) would carry over. Keying on
        // the call identity gives each call a fresh form.
        key={callControlId ?? contactPhone ?? 'new'}
        title="New service request"
        onCancel={onBack}
        onSubmit={handleSubmit}
        onDirtyChange={setIsDirty}
        mode="page"
        // NOT-554 — service requests confirm via SMS + Email by default.
        defaultConfirmationMethod="BOTH"
        customerMode="select"
        initialCustomer={initialCustomer}
        linkedCallControlId={callControlId}
        linkedCallPhone={contactPhone}
        linkedCallAt={callAt}
        serviceSelectionMode="jobType"
        allowUnscheduled
        // Scheduling v2 — this host submits via POST /bookings, which accepts a
        // planned time with no technician (rendered only when the API is v2).
        allowUnassignedManual
        // Scheduling v2 — all-day requests via POST /bookings; multi-day stays
        // hidden without a job (the form gates it on dayJobId).
        allowDayGranularity
        submitLabel="Create"
        initialDateRange={initialDateRange}
        initialTechnicianId={technicianId}
        initialStartAt={startAt}
        onReserveSlot={bookingFlow.reserveSlot}
      />
      <AlertDialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this request?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you leave now, they’ll be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                if (blocker.status === 'blocked') blocker.reset();
              }}
            >
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (blocker.status === 'blocked') blocker.proceed();
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RoutePermissionGuard>
  );
}
